import { useState, useRef, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import { useAiChat } from '../../context/AiChatContext'

const MAX_QUESTION_LENGTH = 4000

/**
 * Transforme les marqueurs [ID:N] de RagFlow en liens markdown cliquables.
 * Ex : "[ID:0]" → "[1](#source-0)"
 */
function preprocessCitations(text) {
  return text.replace(/\[ID:(\d+)\]/g, (_, n) => `[${parseInt(n) + 1}](#source-${n})`)
}

/**
 * Affiche les sources (chunks RagFlow) utilisées pour générer une réponse.
 * Section collapsible avec highlight du chunk actif (depuis une citation cliquée).
 */
function SourcesSection({ sources, activeRefIndex }) {
  const [isOpen, setIsOpen] = useState(false)
  const [expandedDocs, setExpandedDocs] = useState({})
  const activeChunkRef = useRef(null)

  const docAggs = sources?.doc_aggs ?? []
  const chunks = sources?.chunks ?? []

  const docs = docAggs.length > 0
    ? docAggs
    : [...new Map(chunks.map(c => [c.document_name, { doc_name: c.document_name, doc_id: c.document_id, count: 1 }])).values()]

  // Quand une citation est cliquée : ouvrir le panneau, fermer les autres docs,
  // et déplier uniquement celui qui contient le chunk ciblé.
  useEffect(() => {
    if (activeRefIndex == null) return
    const chunk = chunks.find(c => c.ref_index === activeRefIndex)
    if (!chunk) return
    const docKey = chunk.document_id ?? chunk.document_name
    setIsOpen(true)
    setExpandedDocs({ [docKey]: true })
  }, [activeRefIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll vers le chunk actif après rendu
  useEffect(() => {
    if (activeRefIndex == null || !activeChunkRef.current) return
    activeChunkRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [activeRefIndex, isOpen])

  if (!docs.length) return null

  const toggleDoc = (docKey) =>
    setExpandedDocs(prev => ({ ...prev, [docKey]: !prev[docKey] }))

  const getChunksForDoc = (docName) =>
    chunks.filter(c => c.document_name === docName)

  return (
    <div className="mt-1.5 ml-1 max-w-[85%]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-iris-700 transition-colors"
        aria-expanded={isOpen}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
        <span>{docs.length} source{docs.length > 1 ? 's' : ''}</span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="mt-2 space-y-1.5 border border-slate-200 rounded-lg overflow-hidden">
          {docs.map((doc, i) => {
            const docKey = doc.doc_id ?? i
            const docChunks = getChunksForDoc(doc.doc_name)
            const isExpanded = expandedDocs[docKey]
            const hasChunks = docChunks.length > 0
            return (
              <div key={docKey} className="border-b border-slate-100 last:border-0">
                <button
                  onClick={() => hasChunks && toggleDoc(docKey)}
                  className={`w-full flex items-center gap-2 px-3 py-2 bg-slate-50 text-xs text-left transition-colors ${hasChunks ? 'hover:bg-iris-50 cursor-pointer' : 'cursor-default'}`}
                >
                  <svg className="w-3.5 h-3.5 text-iris-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="font-medium text-slate-700 truncate flex-1" title={doc.doc_name}>
                    {doc.doc_name}
                  </span>
                  {doc.count > 1 && (
                    <span className="text-slate-400 shrink-0">{doc.count} extraits</span>
                  )}
                  {hasChunks && (
                    <svg
                      className={`w-3 h-3 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>

                {isExpanded && docChunks.map((chunk, j) => {
                  const isActive = chunk.ref_index === activeRefIndex
                  return (
                    <div
                      key={chunk.id ?? j}
                      ref={isActive ? activeChunkRef : null}
                      className={`px-3 py-2 border-t border-slate-100 text-xs transition-colors ${
                        isActive ? 'bg-iris-50 border-l-2 border-l-iris-400' : 'bg-white'
                      }`}
                    >
                      <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                        {chunk.content}
                      </p>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Enveloppe un message IA et sa section de sources.
 * Gère l'état de la citation active pour coordonner badge ↔ source.
 */
function MessageItem({ msg }) {
  const [activeRefIndex, setActiveRefIndex] = useState(null)

  const handleCitationClick = useCallback((refIndex) => {
    setActiveRefIndex(refIndex)
  }, [])

  // Composant de lien personnalisé pour ReactMarkdown :
  // les liens #source-N deviennent des badges cliquables.
  const citationLink = useCallback(({ href, children }) => {
    if (href?.startsWith('#source-')) {
      const refIndex = parseInt(href.replace('#source-', ''), 10)
      const isActive = activeRefIndex === refIndex
      return (
        <button
          onClick={() => handleCitationClick(refIndex)}
          title={`Voir la source ${children}`}
          className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold mx-0.5 align-middle transition-colors cursor-pointer ${
            isActive
              ? 'bg-iris-800 text-white'
              : 'bg-iris-500 text-white hover:bg-iris-700'
          }`}
        >
          {children}
        </button>
      )
    }
    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  }, [activeRefIndex, handleCitationClick])

  const isUser = msg.role === 'user'
  const hasSources = !isUser && !msg.isError && !msg.isWelcome && msg.sources

  return (
    <div>
      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[85%] rounded-lg px-4 py-2 text-sm leading-relaxed ${
            isUser
              ? 'bg-iris-600 text-white'
              : msg.isError
              ? 'bg-rose-50 text-rose-700 border border-rose-200'
              : msg.isWelcome
              ? 'bg-iris-50 text-iris-800 border border-iris-100'
              : 'bg-slate-100 text-slate-900'
          }`}
        >
          {isUser ? (
            <span className="whitespace-pre-wrap">{msg.content}</span>
          ) : (
            <div className="prose prose-sm max-w-none
              prose-p:my-1 prose-p:leading-relaxed
              prose-headings:font-semibold prose-headings:my-2
              prose-ul:my-1 prose-ul:pl-4
              prose-ol:my-1 prose-ol:pl-4
              prose-li:my-0.5
              prose-strong:font-semibold
              prose-code:bg-white/60 prose-code:px-1 prose-code:rounded prose-code:text-xs
              prose-pre:bg-white/60 prose-pre:rounded prose-pre:p-2 prose-pre:text-xs prose-pre:overflow-x-auto
              [&>*:first-child]:mt-0 [&>*:last-child]:mb-0
            ">
              <ReactMarkdown components={{ a: citationLink }}>
                {hasSources ? preprocessCitations(msg.content) : msg.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      {hasSources && (
        <SourcesSection
          sources={msg.sources}
          activeRefIndex={activeRefIndex}
        />
      )}
    </div>
  )
}

export default function AiChatConversation() {
  const { messages, selectedContext, sessionId, currentPath, addMessage, setSessionId } = useAiChat()
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)
  const abortControllerRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  // Auto-resize du textarea selon le contenu
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [inputValue])

  // Initialisation automatique à l'ouverture du contexte
  useEffect(() => {
    if (sessionId) return

    const initController = new AbortController()
    const run = async () => {
      setIsLoading(true)
      try {
        const response = await fetch('/api/ai-chat/message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ context_id: selectedContext.id, path: currentPath, init: true }),
          signal: initController.signal,
        })
        const contentType = response.headers.get('Content-Type') ?? ''
        if (!response.ok || !contentType.includes('application/json')) return
        const data = await response.json()
        if (data.welcome_message) {
          addMessage({ role: 'assistant', content: data.welcome_message, isWelcome: true, timestamp: Date.now() })
        }
        if (data.session_id) {
          setSessionId(data.session_id)
        }
      } catch (err) {
        if (err.name === 'AbortError') return
      } finally {
        setIsLoading(false)
      }
    }
    run()
    return () => initController.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const handleKeyDown = (e) => {
    // Enter envoie, Shift+Enter ajoute une ligne
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmed = inputValue.trim()
    if (!trimmed || isLoading) return

    if (trimmed.length > MAX_QUESTION_LENGTH) {
      addMessage({
        role: 'assistant',
        content: `La question est trop longue (max ${MAX_QUESTION_LENGTH} caractères).`,
        isError: true,
        timestamp: Date.now(),
      })
      return
    }

    if (!selectedContext?.id) return

    const userMsg = { role: 'user', content: trimmed, timestamp: Date.now() }
    addMessage(userMsg)
    setInputValue('')
    setIsLoading(true)

    abortControllerRef.current = new AbortController()

    // La conversation voyage avec la question : si la session RagFlow est
    // perdue (expirée, serveur redémarré, conversation reprise depuis
    // l'historique local), le backend la rejoue dans une nouvelle session au
    // lieu de repartir de zéro — sans quoi aucune itération n'est possible.
    const history = messages
      .filter((m) => !m.isError && !m.isWelcome)
      .map((m) => ({ role: m.role, content: m.content }))

    // Le chemin voyage avec la question : c'est Drupal qui en déduit les
    // identifiants (projet, entreprise…) transmis à RagFlow.
    const body = {
      context_id: selectedContext.id,
      path: currentPath,
      question: userMsg.content,
      ...(history.length ? { history } : {}),
      ...(sessionId ? { session_id: sessionId } : {}),
    }

    try {
      const response = await fetch('/api/ai-chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: abortControllerRef.current.signal,
      })

      const contentType = response.headers.get('Content-Type') ?? ''
      if (!contentType.includes('application/json')) {
        throw new Error(`Réponse inattendue du serveur (${response.status})`)
      }

      const data = await response.json()

      if (response.ok && data.answer) {
        if (data.welcome_message) {
          addMessage({ role: 'assistant', content: data.welcome_message, isWelcome: true, timestamp: Date.now() })
        }
        addMessage({
          role: 'assistant',
          content: data.answer,
          sources: data.reference ?? null,
          timestamp: Date.now(),
        })
        // Ne jamais effacer la session : la perdre repartirait d'une
        // conversation vierge côté RagFlow à la question suivante.
        setSessionId(data.session_id ?? sessionId)
      } else {
        addMessage({
          role: 'assistant',
          content: 'Une erreur est survenue. Veuillez réessayer.',
          isError: true,
          timestamp: Date.now(),
        })
      }
    } catch (err) {
      if (err.name === 'AbortError') return
      addMessage({
        role: 'assistant',
        content: 'Une erreur est survenue. Veuillez réessayer.',
        isError: true,
        timestamp: Date.now(),
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, idx) => (
          <MessageItem key={`${msg.role}-${msg.timestamp}-${idx}`} msg={msg} />
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 text-slate-500 rounded-lg px-4 py-2 text-sm animate-pulse">
              L'IA réfléchit...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input form */}
      <div className="border-t border-slate-200 p-3">
        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={MAX_QUESTION_LENGTH}
            placeholder="Posez votre question... (Entrée pour envoyer, Maj+Entrée pour sauter une ligne)"
            disabled={isLoading}
            rows={1}
            className="textarea flex-1 resize-none overflow-y-auto leading-relaxed min-h-0"
          />
          <button
            type="submit"
            disabled={isLoading || !inputValue.trim()}
            className="btn btn-primary shrink-0"
          >
            Envoyer
          </button>
        </form>
        {inputValue.length > MAX_QUESTION_LENGTH * 0.9 && (
          <p className="text-xs text-amber-600 mt-1">
            {inputValue.length}/{MAX_QUESTION_LENGTH} caractères
          </p>
        )}
        <p className="text-xs text-slate-400 mt-1">Maj+Entrée pour sauter une ligne</p>
      </div>
    </div>
  )
}
